import React, { useCallback, useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { PageLayout, PageHeader } from '../components/layout';
import { Badge, Button, DataTable, StatePanel } from '../components/ui';
import { useOnboarding } from '../contexts/Onboarding';
import { UserContext } from '../contexts';
import { ProductPicker } from './ProductPicker';
import { Chrono24ImportModal } from './Chrono24ImportModal';
import './import-stock.scss';

/**
 * Bulk stock import.
 *
 * Three steps on one screen: take the template, upload it, see exactly what
 * will happen, then commit. Nothing is written until the merchant presses
 * Import — the preview is a real dry run against the server, not a guess.
 *
 * Import is one of the few genuinely transactional actions in this app, so it
 * keeps an explicit button rather than auto-saving: creating a hundred listings
 * is not something to do on a keystroke.
 */

const STATUS_COPY = {
  ok: 'Ready to import',
  error: 'Needs fixing',
  unmatched: 'Product not found',
  needs_mapping: 'Needs a match',
  duplicate: 'Already in your inventory',
};

function SummaryTile({ tone, count, label }) {
  return (
    <div className="rc-import__tile" data-tone={tone}>
      <span className="rc-import__tilecount">{count}</span>
      <span className="rc-import__tilelabel">{label}</span>
    </div>
  );
}

export function ImportStock() {
  const history = useHistory();
  const { refresh } = useOnboarding();
  const fileInput = useRef(null);

  // The ACTIVE store, not the user's home store — a multi-store owner importing
  // while acting in another store must import into THAT store. Mirrors
  // MarketplaceAccountsPanel; an unset value simply means the accounts hook
  // skips its fetch rather than falling back to the DAO store.
  const { activeStoreId, user } = useContext(UserContext);
  const storeIdM = activeStoreId ?? user?.store_id_m;

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [chrono24Open, setChrono24Open] = useState(false);
  // line -> product id, for rows the merchant mapped by hand.
  const [mappings, setMappings] = useState({});
  const [picking, setPicking] = useState(null);

  /**
   * The template endpoint sits behind auth:sanctum, and this app authenticates
   * with a bearer header rather than a cookie — so a plain <a href> download
   * arrives unauthenticated and fails. Fetch it through axios (which carries
   * the token) and save the blob instead.
   */
  const downloadTemplate = async () => {
    setBusy(true);
    setError('');

    try {
      const { data } = await axios.get('/api/onboarding/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'retail-circle-stock-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('We couldn’t generate the template just now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const post = useCallback(async (path, chosen, chosenMappings = {}) => {
    const form = new FormData();
    form.append('file', chosen);
    Object.entries(chosenMappings).forEach(([line, productId]) => {
      form.append(`mappings[${line}]`, productId);
    });
    const { data } = await axios.post(`/api/onboarding/import/${path}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }, []);

  /**
   * Re-runs the dry run with the merchant's choice folded in, so the counts and
   * the row list update the moment they map something — no separate "apply".
   */
  const applyMapping = async (line, productId) => {
    const next = { ...mappings, [line]: productId };
    setMappings(next);
    setPicking(null);

    if (!file) return;
    setBusy(true);
    try {
      setPreview(await post('preview', file, next));
    } catch (e) {
      setError('We couldn’t re-check your sheet after that change.');
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;

    setFile(chosen);
    setPreview(null);
    setResult(null);
    setError('');
    setMappings({});
    setBusy(true);

    try {
      setPreview(await post('preview', chosen));
    } catch (e) {
      setError(e?.response?.data?.message || 'We couldn’t read that file. Check it’s a CSV saved from the template.');
    } finally {
      setBusy(false);
    }
  };

  const onImport = async () => {
    if (!file) return;
    setBusy(true);
    setError('');

    try {
      // Every product id currently on screen goes back with the request —
      // including the ones matched by AI — so what gets created is exactly what
      // was shown. The server re-validates each id against a real product.
      const displayed = {};
      (preview?.rows || []).forEach((row) => {
        if (row.product_id) displayed[row.line] = row.product_id;
      });

      const data = await post('commit', file, { ...displayed, ...mappings });
      setResult(data);
      refresh();
    } catch (e) {
      setError(e?.response?.data?.message || 'Something went wrong importing your inventory. Nothing was created.');
    } finally {
      setBusy(false);
    }
  };

  const summary = preview?.summary;
  const problemRows = (preview?.rows || []).filter((r) => r.status !== 'ok');

  // Columns for the "rows we can't import yet" preview table — one definition, two
  // presentations (36px table rows on laptop, one card per row on phone) via DataTable.
  const problemColumns = [
    {
      key: 'line',
      header: 'Row',
      card: 'subtitle',
      className: 'rc-import__cellnum',
      cell: (row, meta) => (meta.presentation === 'table' ? row.line : `Row ${row.line}`),
    },
    {
      key: 'summary',
      header: 'Watch',
      card: 'title',
      cell: (row) => row.summary || '—',
    },
    {
      key: 'status',
      header: 'Problem',
      card: 'aside',
      cell: (row) => (
        <Badge status={row.status} size="sm">{STATUS_COPY[row.status] || row.status}</Badge>
      ),
    },
    {
      key: 'action',
      header: 'What to do',
      card: 'meta',
      cell: (row) => (
        <span className="rc-import__cellwhy">
          {row.errors.join(' ')}
          {row.status === 'needs_mapping' || row.status === 'unmatched' ? (
            <span className="rc-import__cellaction">
              <Button variant="outline" size="sm" onClick={() => setPicking(row)}>
                Choose the watch
              </Button>
            </span>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <PageLayout className="rc-import">
      <PageHeader
        title="Import your inventory"
        description="Bring your existing inventory in from a spreadsheet."
        backTo="/rc/listings"
      />

      {result ? (
        <section className="rc-import__done">
          <h2 className="rc-import__donetitle">
            {result.created} {result.created === 1 ? 'listing' : 'listings'} created
          </h2>
          <p className="rc-import__donetext">
            {result.skipped > 0
              ? `${result.skipped} ${result.skipped === 1 ? 'row was' : 'rows were'} skipped. Fix them in your sheet and import again — the ones already created won’t be duplicated.`
              : 'Every row in your sheet is now in your inventory.'}
          </p>
          <div className="rc-import__actions">
            <Button variant="primary" size="lg" onClick={() => history.push('/rc/listings')}>
              View your listings
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => { setResult(null); setPreview(null); setFile(null); }}
            >
              Import another file
            </Button>
          </div>
        </section>
      ) : (
        <>
          <section className="rc-import__step">
            <h2 className="rc-import__steptitle">1. Take the template</h2>
            <p className="rc-import__steptext">
              It has every column we need, with two example watches already filled in. Keep the
              header row exactly as it is and replace the examples with your own stock.
            </p>
            <Button variant="outline" size="lg" onClick={downloadTemplate} disabled={busy}>
              Download the template
            </Button>
          </section>

          <section className="rc-import__step">
            <h2 className="rc-import__steptitle">2. Upload your sheet</h2>
            <p className="rc-import__steptext">
              We’ll check every row and show you what will happen. Nothing is created until you
              press Import.
            </p>

            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="rc-import__file"
              onChange={onPick}
            />
            <Button variant="secondary" size="lg" onClick={() => fileInput.current?.click()} disabled={busy}>
              {file ? `Replace ${file.name}` : 'Choose a CSV file'}
            </Button>

            {busy && !result ? (
              <StatePanel compact state="loading" title="Checking your sheet…" description={null} />
            ) : null}
            {error ? <StatePanel compact state="error" description={error} /> : null}
          </section>

          {summary ? (
            <section className="rc-import__step">
              <h2 className="rc-import__steptitle">3. Check and import</h2>

              <div className="rc-import__tiles">
                <SummaryTile tone="ok" count={summary.ok} label="Ready to import" />
                <SummaryTile tone="warn" count={summary.needs_mapping ?? 0} label="Need you to choose" />
                <SummaryTile tone="warn" count={summary.unmatched} label="Product not found" />
                <SummaryTile tone="warn" count={summary.duplicate} label="Already in stock" />
                <SummaryTile tone="stop" count={summary.error} label="Needs fixing" />
              </div>

              {summary.ai_matched > 0 ? (
                <p className="rc-import__note">
                  {summary.ai_matched} {summary.ai_matched === 1 ? 'row was' : 'rows were'} matched
                  automatically where your sheet was ambiguous. Worth a quick look before importing.
                </p>
              ) : null}

              {summary.catalogue_capped ? (
                <p className="rc-import__note">
                  One of your brands has more products than we can search at once, so some rows may
                  ask you to choose. Picking the watch yourself still works normally.
                </p>
              ) : null}

              {preview.header_missing ? (
                <p className="rc-import__error">
                  We couldn’t find the header row — the line naming the columns, starting with
                  brand. Download the template again and paste your rows underneath its header.
                </p>
              ) : null}

              {preview.unknown_columns?.length ? (
                <p className="rc-import__note">
                  We ignored {preview.unknown_columns.length} column
                  {preview.unknown_columns.length === 1 ? '' : 's'} we didn’t recognise:{' '}
                  {preview.unknown_columns.join(', ')}.
                </p>
              ) : null}

              {summary.truncated ? (
                <p className="rc-import__note">
                  Your file is longer than we can take in one go — only the first rows were read.
                  Split it and import the rest afterwards.
                </p>
              ) : null}

              {problemRows.length ? (
                <div className="rc-import__problems">
                  <h3 className="rc-import__problemstitle">Rows we can’t import yet</h3>
                  <DataTable
                    caption="Rows we can’t import yet"
                    columns={problemColumns}
                    rows={problemRows.slice(0, 50)}
                    rowKey="line"
                  />
                  {problemRows.length > 50 ? (
                    <p className="rc-import__note">
                      Showing the first 50 of {problemRows.length} rows with problems.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="rc-import__actions">
                <Button variant="primary" size="lg" onClick={onImport} disabled={busy || summary.ok === 0}>
                  {busy ? 'Importing…' : `Import ${summary.ok} ${summary.ok === 1 ? 'watch' : 'watches'}`}
                </Button>
                {summary.ok === 0 ? (
                  <span className="rc-import__hint">Fix the rows above, then upload your sheet again.</span>
                ) : null}
              </div>
            </section>
          ) : null}

          {/*
            Chrono24 import. This used to ask for a dealer ACCOUNT NAME, which
            could not be turned into the right dealer page — so the listings
            were never actually pulled across. Now we ask for a link to one of
            the merchant's own listings, which resolves their dealer page in one
            hop, and show them their real inventory to pick from. The same link
            then powers live publish status, so it is only ever asked for once.
          */}
          <section className="rc-import__step rc-import__step--aside">
            <h2 className="rc-import__steptitle">Already selling on Chrono24?</h2>
            <p className="rc-import__steptext">
              Paste a link to one of your own Chrono24 listings and we’ll pull your existing stock
              across, so you don’t have to enter it twice. You choose what comes over, and
              everything arrives as an inactive listing for you to review.
            </p>
            {/* Held back until the Chrono24 pull is confirmed working end to
                end. Left visible so merchants know it is coming, but not
                clickable — a button that half-imports their stock is worse
                than one that isn't there yet. */}
            <div className="rc-import__actions">
              <Button disabled>Import from Chrono24</Button>
              <Badge tone="muted" size="sm">Coming soon</Badge>
            </div>
          </section>
        </>
      )}

      {picking ? (
        <ProductPicker
          row={picking}
          onPick={applyMapping}
          onClose={() => setPicking(null)}
        />
      ) : null}

      {chrono24Open ? (
        <Chrono24ImportModal
          storeIdM={storeIdM}
          onClose={() => setChrono24Open(false)}
          // Imported listings tick the "your stock" checklist item, which is
          // derived server-side from live data.
          onImported={refresh}
        />
      ) : null}
    </PageLayout>
  );
}

export default ImportStock;
